import { ReactElement, useMemo } from 'react';
import {
  Box,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import BigNumber from 'bignumber.js';

import { DECIMAL_PLACES_SHORT } from 'modules/common/const';
import { globalTranslation, useTranslation } from 'modules/i18n';

import { useStyles } from './useStyles';

export interface ITextFieldProps extends MuiTextFieldProps<'filled'> {
  balance?: BigNumber;
  getMoreLink?: string;
  unit?: string;
  onMaxClick?: () => void;
}

export function TextField({
  label,
  balance,
  unit,
  getMoreLink,
  onMaxClick,
  value,
  ...props
}: ITextFieldProps): ReactElement {
  const { t, keys } = useTranslation(globalTranslation);
  const { classes } = useStyles();

  const isShowRightLabel = !!(balance || getMoreLink || onMaxClick);

  const labelContent = useMemo(
    () => (
      <div className={classes.label}>
        <div>{label}</div>

        {isShowRightLabel && (
          <div className={classes.rightLabel}>
            <div className={classes.rightTopLabel}>
              {balance && (
                <div className={classes.balance}>
                  {t(keys.common.balance)}

                  {/* eslint-disable-next-line @typescript-eslint/restrict-template-expressions */}
                  {`: `}

                  <Box
                    color={theme => theme.palette.text.primary}
                    component="span"
                  >
                    {t(keys.unit.tokenValue, {
                      value: balance
                        .decimalPlaces(DECIMAL_PLACES_SHORT)
                        .toFormat(),
                      token: unit || '',
                    })}
                  </Box>
                </div>
              )}

              {getMoreLink && (
                <a
                  className={classes.link}
                  href={getMoreLink}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t(keys.common.getMore)}
                </a>
              )}
            </div>

            {onMaxClick && (
              <Box
                className={classes.link}
                textTransform="uppercase"
                onClick={onMaxClick}
              >
                {t(keys.common.max)}
              </Box>
            )}
          </div>
        )}
      </div>
    ),
    [
      balance,
      classes.balance,
      classes.label,
      classes.link,
      classes.rightLabel,
      classes.rightTopLabel,
      getMoreLink,
      isShowRightLabel,
      keys.common.balance,
      keys.common.getMore,
      keys.common.max,
      keys.unit.tokenValue,
      label,
      onMaxClick,
      t,
      unit,
    ],
  );

  return (
    <MuiTextField
      inputMode="decimal"
      label={labelContent}
      type="text"
      {...props}
      value={typeof value === 'string' ? value : String(value ?? '')}
    />
  );
}
